import React, { useState, useEffect } from 'react';
import { Timeline, Card, Row, Col, Button, Pagination } from 'antd';
import moment from 'moment';
import { incidentService } from '@iso/services';
import IntlMessages from '@iso/components/utility/intlMessages';
import IncidentHistoryModalWrapper from './IncidentHistoryModal.styles';
import {
  findIncidentTypeOption,
  findIncidentOutcomeOption,
  findIncidentStatusOption,
  INVESTIGATION_TYPES,
} from '@iso/constants/incidentOptions';

const investigationTypeLabel = (value) =>
  INVESTIGATION_TYPES.find((t) => t.value === value)?.label || value;
const statusLabel = (value) => findIncidentStatusOption(value)?.label || value;

const IncidentHistoryModal = ({ visible, onCancel, incident }) => {
  const [histories, setHistories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    totalCount: 0,
    pageCount: 0,
  });

  useEffect(() => {
    const fetchHistories = async () => {
      try {
        const response = await incidentService.getHistories({
          incident_id: incident.id,
          page: pagination.currentPage,
          per_page: pagination.perPage,
          expand: 'user',
        });
        setHistories(response.items);
        setPagination(response._meta);
        setLoading(false);
      } catch (error) {
        console.error('There was an error fetching the incident history!', error);
        setLoading(false);
      }
    };

    if (incident && incident.id) {
      fetchHistories();
    }
  }, [incident]);

  const handlePageChange = (page, pageSize) => {
    setPagination({ ...pagination, currentPage: page, perPage: pageSize });
  };

  const renderHistoryItem = (label, value) => {
    if (!value) return <></>;
    return (
      <Row>
        <Col xs={{ span: 11 }} md={{ span: 11 }}>
          <p className="card_content">{label}</p>
        </Col>
        <Col xs={{ span: 11, offset: 2 }} md={{ span: 11, offset: 2 }}>
          <b className="card_value">{value}</b>
        </Col>
      </Row>
    );
  };

  return (
    incident &&
    <IncidentHistoryModalWrapper
      visible={visible}
      onCancel={onCancel}
      title={<>{<IntlMessages id="incidentPage.history.title" />} ~ {incident.report_number}</>}
      footer={[
        <Button key="back" onClick={onCancel} className="cancel-button">
          {<IntlMessages id="propertyPage.modal.cancel" />}
        </Button>,
      ]}
      width={800}
    >
      <div style={{ overflow: 'scroll', width: '100%', height: '600px' }}>
        <br />
        <div className="form-body">
          <Timeline>
            {histories.length > 0 ? histories.map((value, index) => (
              <Timeline.Item color="green" key={index}>
                <p>{moment(value.created_at).format('ll')}</p>
                <Card title={<IntlMessages id={`incidentPage.history.actionTitle.${value.action}`} />} bordered={true}>
                  {value.action === 'reclassified' && (
                    <>
                      {renderHistoryItem('Investigation type', `${investigationTypeLabel(value.metadata?.from_type)} → ${investigationTypeLabel(value.metadata?.to_type)}`)}
                      {renderHistoryItem('Status', `${statusLabel(value.metadata?.previous_status)} → ${statusLabel(value.metadata?.new_status)}`)}
                    </>
                  )}
                  {renderHistoryItem('Name', value.metadata?.name)}
                  {renderHistoryItem('Quantity', value.metadata?.quantity)}
                  {renderHistoryItem('Application', value.metadata?.application)}
                  {renderHistoryItem('Description', value.metadata?.description)}
                  {renderHistoryItem('Date Time', value.metadata?.date_time ? moment(value.metadata.date_time).format("DD/MM/YYYY HH:mm") : null)}
                  {renderHistoryItem('Type', (() => {
                    if (!value.metadata?.type) {
                      return null;
                    }
                    const typeOption = findIncidentTypeOption(value.metadata.type);
                    return typeOption ? typeOption.label : value.metadata.type;
                  })())}
                  {renderHistoryItem('Status', (() => {
                    if (!value.metadata?.status) {
                      return null;
                    }
                    const statusOption = findIncidentStatusOption(value.metadata.status);
                    if (!statusOption) {
                      return value.metadata.status;
                    }
                    return statusOption.description
                      ? `${statusOption.label} – ${statusOption.description}`
                      : statusOption.label;
                  })())}
                  {renderHistoryItem('Activity', value.metadata?.activity)}
                  {renderHistoryItem('Outcome', (() => {
                    if (!value.metadata?.consequence) {
                      return null;
                    }
                    const outcomeOption = findIncidentOutcomeOption(value.metadata.consequence);
                    return outcomeOption ? outcomeOption.label : value.metadata.consequence;
                  })())}
                  {renderHistoryItem('Contractor/Visitor Involved', value.metadata?.contractor_visitor_involved)}
                  {renderHistoryItem('Latitude', value.metadata?.latitude)}
                  {renderHistoryItem('Longitude', value.metadata?.longitude)}
                  {renderHistoryItem('Details of any injury', value.metadata?.injury_details)}
                  {renderHistoryItem('Details of any damage', value.metadata?.damage_details)}
                  {renderHistoryItem('Details of any impact to environment', value.metadata?.environment_impact_details)}
                  {renderHistoryItem('Immediate actions taken following the event', value.metadata?.immediate_actions)}
                  {renderHistoryItem('Personnel reported to', value.metadata?.personnel_notified)}
                  {renderHistoryItem('How was the scene secured', value.metadata?.scene_secured)}
                  {renderHistoryItem('Was an SOP or JSA in place for this task', value.metadata?.risk_assessment_conducted)}
                  {renderHistoryItem('Reason why an SOP or JSA was not in place', value.metadata?.risk_assessment_not_completed_reason)}
                  {renderHistoryItem('Details of the SOP or JSA in place', value.metadata?.risk_assessment_details)}
                  {renderHistoryItem('Additional controls or policies applied', value.metadata?.sop_policy_details)}

                  {renderHistoryItem('By User', value.user?.name)}
                </Card>
              </Timeline.Item>
            )) : <p style={{marginBottom: 16}}>No history found!</p>}
          </Timeline>

          <Pagination
            current={pagination.currentPage}
            total={pagination.totalCount}
            pageSize={pagination.perPage}
            onChange={handlePageChange}
            showSizeChanger={true}
            pageSizeOptions={["10", "20", "50", "100"]}
          />
        </div>
      </div>
    </IncidentHistoryModalWrapper>
  );
};

export default IncidentHistoryModal;
